import { makeStyles } from "@fluentui/react-components";
import { primer } from "./primerDarkTheme";

export const useActivityLogPopupStyles = makeStyles({
    /* ── Overlay ─────────────────────────────────────── */
    overlay: {
        position: "fixed",
        inset: "0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.55)",
        zIndex: 1000,
    },

    /* ── Modal shell ─────────────────────────────────── */
    modal: {
        backgroundColor: primer.surface,
        border: `1px solid ${primer.border}`,
        borderRadius: "12px",
        boxShadow: "0px 8px 32px rgba(0,0,0,0.5)",
        display: "flex",
        flexDirection: "column",
        width: "70vw",
        maxWidth: "1200px",
        height: "86vh",
        maxHeight: "860px",
        overflow: "clip",
    },

    /* ── Header ──────────────────────────────────────── */
    header: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "14px 20px",
        flexShrink: 0,
    },
    titleGroup: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexShrink: 0,
    },
    title: {
        fontSize: "16px",
        fontWeight: 600,
        color: primer.fg,
        whiteSpace: "nowrap",
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: primer.overlay,
        borderRadius: "9px",
        padding: "1px 7px",
        fontSize: "11px",
        color: primer.fgMuted,
    },

    /* Search */
    searchBar: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        backgroundColor: primer.canvas,
        border: `1px solid ${primer.border}`,
        borderRadius: "6px",
        padding: "4px 10px",
        width: "300px",
        flexShrink: 0,
    },
    searchIcon: {
        fontSize: "14px",
        color: primer.fgMuted,
        flexShrink: 0,
    },
    searchInput: {
        backgroundColor: "transparent",
        border: "none",
        outline: "none",
        color: primer.fg,
        fontSize: "13px",
        flex: 1,
        "::placeholder": { color: primer.fgSubtle },
    },

    /* Filter chips */
    chipGroup: {
        display: "flex",
        gap: "6px",
        alignItems: "center",
        flex: 1,
        minWidth: 0,
    },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: 500,
        cursor: "pointer",
        whiteSpace: "nowrap",
        border: `1px solid ${primer.border}`,
        backgroundColor: primer.overlay,
        color: primer.fgMuted,
    },
    chipActive: {
        backgroundColor: primer.fg,
        color: primer.surface,
        border: `1px solid ${primer.fg}`,
        fontWeight: 600,
    },
    chipScout: { color: "#56d364" },
    chipHeal: { color: primer.blue },

    /* Close button */
    closeBtn: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "24px",
        height: "24px",
        borderRadius: "4px",
        border: "none",
        backgroundColor: "transparent",
        color: primer.fgMuted,
        cursor: "pointer",
        fontSize: "14px",
        flexShrink: 0,
        ":hover": { color: primer.fg },
    },

    /* ── Divider ─────────────────────────────────────── */
    divider: {
        height: "1px",
        width: "100%",
        backgroundColor: primer.overlay,
        flexShrink: 0,
    },

    /* ── Item list ───────────────────────────────────── */
    list: {
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
    },

    /* ── Single activity item ────────────────────────── */
    item: {
        display: "flex",
        alignItems: "flex-start",
        padding: "10px 16px 10px 20px",
        borderBottom: `1px solid ${primer.overlay}`,
    },
    itemContent: {
        flex: 1,
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        minWidth: 0,
    },
    itemTextGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
    },
    itemMeta: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        fontSize: "11px",
    },
    titleRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
    },
    tag: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2px 6px",
        borderRadius: "4px",
        fontSize: "10px",
        fontWeight: 600,
        flexShrink: 0,
    },
    // Only scout/heal are emitted by the activity-log API (see
    // ActivityItemData in Sidebar.tsx). Add new tag styles here only when
    // the API filter is widened.
    tagScout: {
        backgroundColor: "#1a4d2e",
        color: "#56d364",
    },
    tagHeal: {
        backgroundColor: "#1f3554",
        color: primer.blue,
    },
    itemTitle: {
        fontSize: "13px",
        fontWeight: 500,
        color: primer.fg,
    },
    itemDetail: {
        fontSize: "11px",
        color: primer.fgMuted,
    },
    prLink: {
        display: "flex",
        gap: "4px",
        alignItems: "center",
        fontSize: "11px",
        color: primer.blue,
        cursor: "pointer",
        textDecoration: "none",
        ":hover": { textDecoration: "underline" },
    },
    prLinkInert: {
        display: "flex",
        gap: "4px",
        alignItems: "center",
        fontSize: "11px",
        color: primer.fgMuted,
    },
    prNumber: { fontWeight: 500 },
    prTitle: { fontWeight: 400 },
    itemTime: {
        fontSize: "10px",
        color: primer.fgMuted,
        whiteSpace: "nowrap",
        flexShrink: 0,
        paddingTop: "2px",
    },

    /* ── Empty state ─────────────────────────────────── */
    emptyState: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        fontSize: "13px",
        color: primer.fgSubtle,
    },
});
